import { useForgotPassword } from '@/pages/ResetPassword/api/useForgotPassword';
import { useRedirectStore } from '@/stores/redirect';
import { useForm } from '@tanstack/react-form';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

export type LoginMode = 'sso' | 'password';

/** Non-presentational login-form logic, testable without mounting the design-system tree. */
export function useLoginForm() {
  const { t } = useTranslation('login');
  const { handleForgotPassword } = useForgotPassword();
  const search = useSearch({ from: '/login' });

  const initialEmail = search?.email || '';
  const initialMode: LoginMode = search?.mode === 'password' ? 'password' : 'sso';

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mode, setMode] = useState<LoginMode>(initialMode);

  const setRedirectPath = useRedirectStore(useShallow(state => state.setRedirectPath));

  useEffect(() => {
    if (search?.redirect) {
      setRedirectPath(search.redirect);
    }
  }, [search?.redirect, setRedirectPath]);

  const userSchema = useMemo(
    () =>
      z.object({
        email: z.email(t('form.labels.invalid_email')).min(1, t('form.labels.email_required')),
        // Only rendered/submitted in password mode; unconstrained (not required)
        // in SSO mode so it can't gate submission.
        password: mode === 'password' ? z.string().min(1, t('form.labels.pass_required')) : z.string(),
      }),
    [mode, t],
  );

  const form = useForm({
    defaultValues: {
      email: initialEmail,
      password: '',
    },
    validators: {
      onChange: userSchema,
    },
  });

  // Resync when /login is already mounted and only its search params change
  // (e.g. back-navigation from reset-password). Only syncs forward — clearing
  // the params doesn't clear the email or reset the mode.
  useEffect(() => {
    if (initialEmail) {
      form.setFieldValue('email', initialEmail);
    }
    if (initialMode === 'password') {
      // oxlint-disable-next-line set-state-in-effect -- resyncs from router search params (external), mode is also directly user-toggled so not derivable
      setMode('password');
    }
  }, [form, initialEmail, initialMode]);

  const toggleMode = useCallback(() => setMode(current => (current === 'password' ? 'sso' : 'password')), []);
  const markSubmitted = useCallback(() => setIsSubmitted(true), []);

  // A bfcache restore (e.g. back-navigating after a failed submit) can leave
  // `isSubmitted` stuck `true`, wedging the submit button in its spinner
  // state. `event.persisted` is what distinguishes that from a fresh load.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setIsSubmitted(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  return { form, handleForgotPassword, isSubmitted, markSubmitted, mode, toggleMode };
}
